import * as React from 'react';
import { Box, Breadcrumbs, Link, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { EnvelopeSimple as EnvelopeIcon } from '@phosphor-icons/react/dist/ssr/EnvelopeSimple';
import { useNavigate } from 'react-router-dom';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { UserAvatar } from '@/components/common/user-avatar';

interface UserHeaderProps {
  resident?: User | null;
  isLoading: boolean;
}

export function UserHeader({ resident, isLoading }: UserHeaderProps): React.JSX.Element {
  const navigate = useNavigate();

  const displayName = resident
    ? [resident.first_name, resident.last_name].filter(Boolean).join(' ').trim() || resident.email || 'User'
    : '';

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton height={20} width={200} variant="text" />
        <Skeleton height={36} width={400} variant="text" />
        <Skeleton height={20} width={300} variant="text" />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem', mx: -0.5 }}>/</Typography>}
        sx={{ fontSize: '0.8125rem' }}
      >
        <Link
          component="button"
          onClick={() => navigate(paths.users.index)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: 'inherit',
            '&:hover': { color: 'text.primary' },
          }}
          underline="none"
        >
          <ArrowLeftIcon size={13} />
          Users
        </Link>
        <Typography color="text.primary" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
          {displayName || 'User'}
        </Typography>
      </Breadcrumbs>

      {/* Title row */}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <UserAvatar size="48px" user={resident} />
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }} variant="h4">
              {displayName || 'User'}
            </Typography>
            {/* Meta row */}
            {resident?.email && (
              <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                  <EnvelopeIcon size={13} />
                  <Typography variant="caption">{resident.email}</Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
